/**
 * Simple openings editor: doors / windows / cutouts on each wall.
 * Persists to project.wall_openings.
 */
import { X, Plus, Trash, Door, AppWindow, Crop } from "@phosphor-icons/react";

const KIND_OPTS = [
  { v: "door",   label: "Door",   icon: Door },
  { v: "window", label: "Window", icon: AppWindow },
  { v: "cutout", label: "Cutout", icon: Crop },
];
const WALL_OPTS = [
  { v: "back",  label: "Back (Wall A)" },
  { v: "right", label: "Right (Wall B)" },
  { v: "front", label: "Front (Wall C)" },
  { v: "left",  label: "Left (Wall D)" },
];

const DEFAULTS = {
  door:   { w_in: 36, h_in: 84, y_in: 0 },
  window: { w_in: 48, h_in: 36, y_in: 36 },
  cutout: { w_in: 24, h_in: 24, y_in: 60 },
};

export default function OpeningsEditor({ open, onClose, openings, onChange }) {
  // Fully controlled — read from `openings` (the source of truth) and write
  // back via `onChange`. Avoids the prior bug where local state was stale
  // when the project loaded asynchronously after this component mounted.
  if (!open) return null;
  const list = openings || [];
  const persist = (next) => { onChange(next); };

  const add = () => {
    persist([...list, {
      id: `op-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
      wall: "back", kind: "door",
      x_in: 24, ...DEFAULTS.door,
    }]);
  };

  const updateOne = (idx, patch) => {
    const next = list.slice();
    next[idx] = { ...next[idx], ...patch };
    if (patch.kind) next[idx] = { ...next[idx], ...DEFAULTS[patch.kind] };
    persist(next);
  };

  const remove = (idx) => {
    const next = list.slice(); next.splice(idx, 1); persist(next);
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-6"
      onClick={onClose}
      data-testid="openings-editor"
    >
      <div className="bg-[#0a0d12] border border-[#00E5FF]/30 w-full max-w-3xl max-h-[88vh] flex flex-col"
           onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between p-4 border-b border-white/10">
          <div>
            <div className="label-tag">// 3D · openings</div>
            <h3 className="font-display text-lg font-bold">Doors, windows &amp; cutouts</h3>
            <p className="text-xs text-gray-400 mt-1">
              Coordinates are inches along the wall (X), and inches from the floor (Y bottom).
            </p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-white p-1" data-testid="openings-close">
            <X size={18} />
          </button>
        </div>
        <div className="overflow-auto p-4 flex-1" data-testid="openings-body">
          {list.length === 0 && (
            <div className="text-xs text-gray-500 font-mono py-4 text-center">
              No openings yet — click + Add below.
            </div>
          )}
          <ul className="space-y-2">
            {list.map((op, idx) => (
              <li key={op.id || idx}
                  className="grid grid-cols-[100px_150px_60px_60px_60px_60px_30px] gap-2 items-center text-xs font-mono"
                  data-testid={`opening-row-${idx}`}>
                <select value={op.kind || "door"} onChange={(e) => updateOne(idx, { kind: e.target.value })}
                        className="bg-black/40 border border-white/10 px-2 py-1">
                  {KIND_OPTS.map((k) => <option key={k.v} value={k.v}>{k.label}</option>)}
                </select>
                <select value={op.wall || "back"} onChange={(e) => updateOne(idx, { wall: e.target.value })}
                        className="bg-black/40 border border-white/10 px-2 py-1">
                  {WALL_OPTS.map((w) => <option key={w.v} value={w.v}>{w.label}</option>)}
                </select>
                <input type="number" value={op.x_in ?? 0} placeholder="X"
                       onChange={(e) => updateOne(idx, { x_in: parseFloat(e.target.value) || 0 })}
                       className="bg-black/40 border border-white/10 px-2 py-1" />
                <input type="number" value={op.y_in ?? 0} placeholder="Y"
                       onChange={(e) => updateOne(idx, { y_in: parseFloat(e.target.value) || 0 })}
                       className="bg-black/40 border border-white/10 px-2 py-1" />
                <input type="number" value={op.w_in ?? 36} placeholder="W"
                       onChange={(e) => updateOne(idx, { w_in: parseFloat(e.target.value) || 0 })}
                       className="bg-black/40 border border-white/10 px-2 py-1" />
                <input type="number" value={op.h_in ?? 80} placeholder="H"
                       onChange={(e) => updateOne(idx, { h_in: parseFloat(e.target.value) || 0 })}
                       className="bg-black/40 border border-white/10 px-2 py-1" />
                <button onClick={() => remove(idx)} className="text-red-400 hover:text-red-300" data-testid={`opening-remove-${idx}`}>
                  <Trash size={14} />
                </button>
              </li>
            ))}
          </ul>
          <div className="mt-4 grid grid-cols-[100px_150px_60px_60px_60px_60px_30px] gap-2 text-[10px] uppercase tracking-wider text-gray-500 font-mono">
            <div>Kind</div><div>Wall</div><div>X in</div><div>Y in</div><div>W in</div><div>H in</div><div /></div>
          <button onClick={add} className="btn-primary mt-4 text-xs py-1.5 inline-flex items-center gap-2"
                  data-testid="openings-add">
            <Plus size={14} weight="bold" /> Add opening
          </button>
        </div>
      </div>
    </div>
  );
}
